import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ConfirmPopup from "../../../../../views/components/common/pop-up/ConfirmPopup";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import {
  SwayyOrderNote,
  SwayyOrderNoteImage,
} from "../../../../../models/SwayyOrder/SwayyOrderDetail";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { useStores } from "../../../use-stores";

const getInitials = (name?: string | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
  ).toUpperCase();
};

const AVATAR_COLORS = [
  "#0BB6AE",
  "#005154",
  "#1E88E5",
  "#00897B",
  "#E53935",
  "#FB8C00",
  "#3949AB",
  "#00ACC1",
  "#43A047",
  "#F4511E",
  "#6D4C41",
  "#D81B60",
];

const avatarColor = (name?: string | null): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatActivityTime = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const UserAvatar: React.FC<{ name?: string | null; size?: number }> = ({
  name,
  size = 36,
}) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.4,
      fontWeight: 600,
      bgcolor: avatarColor(name),
      flexShrink: 0,
      fontFamily: swayyFont,
    }}
  >
    {getInitials(name)}
  </Avatar>
);

const NoteImages: React.FC<{ images?: SwayyOrderNoteImage[] }> = ({
  images,
}) => {
  if (!images?.length) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        mt: 1,
      }}
    >
      {images.map((image, index) => (
        <Box
          key={image.id ?? `${image.url}-${index}`}
          component="a"
          href={image.url}
          target="_blank"
          rel="noreferrer"
          sx={{
            width: 72,
            height: 72,
            borderRadius: swayyRadii.sm,
            overflow: "hidden",
            border: `1px solid ${swayyColors.border}`,
            display: "block",
          }}
        >
          <Box
            component="img"
            src={image.url}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      ))}
    </Box>
  );
};

interface NoteComposerProps {
  busy?: boolean;
  avatarName?: string | null;
  initialMessage?: string;
  initialImages?: SwayyOrderNoteImage[];
  submitLabel?: string;
  onCancel?: () => void;
  onSubmit: (message: string, files: File[], keepUrls: string[]) => Promise<void> | void;
}

const NOTE_IMAGE_MAX_BYTES = 1 * 1024 * 1024; // 1 MB
const NOTE_IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

const NoteComposer: React.FC<NoteComposerProps> = ({
  busy = false,
  avatarName,
  initialMessage = "",
  initialImages = [],
  submitLabel = "Add note",
  onCancel,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState(initialMessage);
  const [files, setFiles] = useState<File[]>([]);
  const [keepUrls, setKeepUrls] = useState<string[]>(
    initialImages.map((image) => image.url).filter(Boolean),
  );
  const [attachError, setAttachError] = useState<string | null>(null);
  const canSubmit = message.trim().length > 0 && !busy;

  const handleFilesSelected = (selected: File[]) => {
    if (!selected.length) return;
    setAttachError(null);

    const oversized = selected.filter((file) => file.size > NOTE_IMAGE_MAX_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map((file) => file.name).join(", ");
      setAttachError(
        `Image must be 1 MB or less. Rejected: ${names}`,
      );
    }

    const accepted = selected.filter(
      (file) => file.size <= NOTE_IMAGE_MAX_BYTES,
    );
    if (!accepted.length) return;

    setFiles((prev) => [...prev, ...accepted].slice(0, 10));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await onSubmit(message.trim(), files, keepUrls);
      setMessage("");
      setFiles([]);
      setKeepUrls([]);
      setAttachError(null);
    } catch {
      // Parent handles notification; keep draft on failure.
    }
  };

  const previewUrls = files.map((file) => URL.createObjectURL(file));

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <UserAvatar name={avatarName} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            border: `1px solid ${swayyColors.border}`,
            borderRadius: swayyRadii.md,
            px: 1.5,
            py: 1,
            backgroundColor: swayyColors.white,
            transition: "border-color .15s ease, box-shadow .15s ease",
            "&:focus-within": {
              borderColor: swayyColors.pine,
              boxShadow: `0 0 0 3px ${alpha(swayyColors.pine, 0.12)}`,
            },
          }}
        >
          <InputBase
            multiline
            minRows={2}
            placeholder="Add a note..."
            value={message}
            disabled={busy}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            sx={{
              fontFamily: swayyFont,
              fontSize: 14,
              color: swayyColors.deepGreen,
              "& textarea": { lineHeight: 1.55 },
            }}
          />

          {(keepUrls.length > 0 || previewUrls.length > 0) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {keepUrls.map((url) => (
                <Box
                  key={url}
                  sx={{
                    position: "relative",
                    width: 64,
                    height: 64,
                    borderRadius: swayyRadii.sm,
                    overflow: "hidden",
                    border: `1px solid ${swayyColors.border}`,
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      setKeepUrls((prev) => prev.filter((item) => item !== url))
                    }
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 22,
                      height: 22,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              {previewUrls.map((url, index) => (
                <Box
                  key={`${url}-${index}`}
                  sx={{
                    position: "relative",
                    width: 64,
                    height: 64,
                    borderRadius: swayyRadii.sm,
                    overflow: "hidden",
                    border: `1px solid ${swayyColors.border}`,
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 22,
                      height: 22,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${swayyColors.border}`,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={NOTE_IMAGE_ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                const selected = Array.from(e.target.files ?? []);
                handleFilesSelected(selected);
                e.target.value = "";
              }}
            />
            <Tooltip title="Attach images (max 1 MB each)">
              <span>
                <IconButton
                  size="small"
                  disabled={busy || keepUrls.length + files.length >= 10}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: swayyColors.pine }}
                >
                  <ImageOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {onCancel ? (
              <AppButton
                label="Cancel"
                disabled={busy}
                onClick={onCancel}
                customStyles={{
                  minHeight: 40,
                  fontSize: "0.75rem",
                  px: 2,
                  borderRadius: swayyRadii.sm,
                  bgcolor: swayyColors.pageBg,
                  color: swayyColors.deepGreen,
                  fontFamily: swayyFont,
                  "&:hover": { bgcolor: swayyColors.border },
                }}
              />
            ) : null}
            <AppButton
              label={busy ? "Saving…" : submitLabel}
              disabled={!canSubmit}
              onClick={handleSubmit}
              customStyles={{
                minHeight: 40,
                fontSize: "0.75rem",
                px: 2,
                borderRadius: swayyRadii.sm,
                bgcolor: swayyColors.pine,
                color: swayyColors.white,
                fontFamily: swayyFont,
                "&:hover": { bgcolor: swayyColors.pineHover },
                "&.Mui-disabled": {
                  bgcolor: swayyColors.border,
                  color: swayyColors.textSecondary,
                },
              }}
            />
          </Box>
          {attachError ? (
            <CustomTypography
              variant="body3"
              color={swayyColors.danger}
              sx={{ fontFamily: swayyFont, mt: 1, fontSize: 12 }}
            >
              {attachError}
            </CustomTypography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

interface NoteItemProps {
  note: SwayyOrderNote;
  currentUserName?: string | null;
  isCustomerNote?: boolean;
  busy?: boolean;
  onEdit?: (note: SwayyOrderNote) => void;
  onDelete?: (note: SwayyOrderNote) => void;
}

const NoteItem: React.FC<NoteItemProps> = ({
  note,
  currentUserName,
  isCustomerNote = false,
  busy = false,
  onEdit,
  onDelete,
}) => {
  const isDeleted = Boolean(note.is_deleted);
  const hasUpdatedTimestamp =
    Boolean(note.updated_at) &&
    Boolean(note.created_at) &&
    note.updated_at !== note.created_at;
  const isEdited =
    !isDeleted && (Boolean(note.is_edited) || hasUpdatedTimestamp);
  const author = isCustomerNote
    ? "Customer"
    : note.created_by || "Unknown";
  const isOwn =
    !isCustomerNote &&
    !isDeleted &&
    (note.can_modify === true ||
      Boolean(
        currentUserName &&
          note.created_by &&
          note.created_by.trim().toLowerCase() ===
            currentUserName.trim().toLowerCase(),
      ));
  const canModify = !isCustomerNote && !isDeleted && note.can_modify === true;

  const bubbleBg = isDeleted
    ? alpha(swayyColors.textSecondary, 0.06)
    : isOwn
      ? alpha(swayyColors.pine, 0.06)
      : isCustomerNote
        ? swayyColors.pageBg
        : "#F7FAFB";
  const bubbleBorder = isDeleted
    ? swayyColors.border
    : isOwn
      ? alpha(swayyColors.pine, 0.25)
      : swayyColors.border;

  return (
    <Box sx={{ display: "flex", gap: 1.5, py: 1 }}>
      <UserAvatar name={author} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            border: `1px solid ${bubbleBorder}`,
            backgroundColor: bubbleBg,
            borderRadius: swayyRadii.md,
            px: 1.75,
            py: 1.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={600}
              color={swayyColors.deepGreen}
              sx={{ fontFamily: swayyFont, fontSize: 14 }}
            >
              {author}
            </CustomTypography>
            {isOwn ? (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.6,
                  px: 0.75,
                  borderRadius: swayyRadii.xs,
                  color: swayyColors.pine,
                  backgroundColor: alpha(swayyColors.pine, 0.12),
                  fontFamily: swayyFont,
                }}
              >
                You
              </Box>
            ) : null}
            {isCustomerNote ? (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.6,
                  px: 0.75,
                  borderRadius: swayyRadii.xs,
                  color: swayyColors.textSecondary,
                  backgroundColor: alpha(swayyColors.textSecondary, 0.1),
                  fontFamily: swayyFont,
                }}
              >
                Order note
              </Box>
            ) : null}
            {isEdited ? (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.6,
                  px: 0.75,
                  borderRadius: swayyRadii.xs,
                  color: swayyColors.textSecondary,
                  backgroundColor: alpha(swayyColors.textSecondary, 0.1),
                  fontFamily: swayyFont,
                }}
              >
                Edited
              </Box>
            ) : null}
            
            {!isCustomerNote && note.created_at ? (
              <CustomTypography
                variant="caption"
                color={swayyColors.textSecondary}
                sx={{ fontFamily: swayyFont, fontSize: 12 }}
              >
                {formatActivityTime(
                  isDeleted ? note.deleted_at || note.created_at : note.created_at,
                )}
              </CustomTypography>
            ) : null}
            {canModify ? (
              <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
                {onEdit ? (
                  <Tooltip title="Edit">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onEdit(note)}
                        sx={{ color: swayyColors.textSecondary }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                {onDelete ? (
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onDelete(note)}
                        sx={{ color: swayyColors.textSecondary }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            ) : null}
          </Box>

          {isDeleted ? (
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
              sx={{
                mt: 0.5,
                fontFamily: swayyFont,
                fontSize: 14,
                fontStyle: "italic",
                lineHeight: 1.55,
              }}
            >
              This note has been deleted.
            </CustomTypography>
          ) : (
            <>
              <CustomTypography
                variant="body2"
                color={swayyColors.deepGreen}
                sx={{
                  mt: 0.5,
                  fontFamily: swayyFont,
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.55,
                }}
              >
                {note.note}
              </CustomTypography>
              <NoteImages images={note.images} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface SwayyOrderNotesPanelProps {
  customerNote?: string | null;
}

const SwayyOrderNotesPanel: React.FC<SwayyOrderNotesPanelProps> = ({
  customerNote,
}) => {
  const { swayyOrderDetailStore: store } = useStores();
  const { showError } = useNotification();
  const currentUserName = userStore.user?.name ?? null;
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [notePendingDelete, setNotePendingDelete] =
    useState<SwayyOrderNote | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const notes = store.notes;

  const handleAddNote = async (
    message: string,
    files: File[],
    _keepUrls: string[],
  ) => {
    try {
      await store.addNote(message, files);
    } catch (error: unknown) {
      const errMessage =
        error instanceof Error ? error.message : "Failed to add note.";
      showError("Error", errMessage);
      throw error;
    }
  };

  const handleUpdateNote = async (
    noteId: number | string,
    message: string,
    files: File[],
    keepUrls: string[],
  ) => {
    try {
      const updated = await store.updateNote(noteId, message, files, keepUrls);
      if (updated) {
        setEditingNoteId(null);
      }
    } catch (error: unknown) {
      const errMessage =
        error instanceof Error ? error.message : "Failed to update note.";
      showError("Error", errMessage);
      throw error;
    }
  };

  const handleOpenDeleteNote = (note: SwayyOrderNote) => {
    setDeleteError(null);
    setNotePendingDelete(note);
  };

  const handleCloseDeleteNote = () => {
    if (store.noteSaving) return;
    setNotePendingDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDeleteNote = async () => {
    if (!notePendingDelete) return;
    try {
      setDeleteError(null);
      const deleted = await store.deleteNote(notePendingDelete.id);
      if (deleted) {
        if (editingNoteId === String(notePendingDelete.id)) {
          setEditingNoteId(null);
        }
        setNotePendingDelete(null);
      }
    } catch (error: unknown) {
      const errMessage =
        error instanceof Error ? error.message : "Failed to delete note.";
      setDeleteError(errMessage);
    }
  };

  const canEditNotes = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_ORDERS,
  );
  const hasCustomerNote = Boolean(customerNote?.trim());
  const hasNotes = notes.length > 0;
  const editingNote = canEditNotes
    ? notes.find(
        (note) =>
          String(note.id) === editingNoteId &&
          !note.is_deleted &&
          note.can_modify === true,
      )
    : undefined;

  return (
    <Box>
      <CustomTypography
        variant="heading5"
        color={swayyColors.deepGreen}
        sx={{
          mb: 1,
          fontFamily: swayyFont,
          fontWeight: 700,
          fontSize: 16,
        }}
      >
        Notes
      </CustomTypography>
      <Divider sx={{ mb: 2, borderColor: swayyColors.border }} />

      {canEditNotes ? (
        <Box sx={{ mb: 2 }}>
          {editingNote ? (
            <NoteComposer
              key={`edit-${editingNote.id}`}
              avatarName={currentUserName}
              busy={store.noteSaving}
              initialMessage={editingNote.note}
              initialImages={editingNote.images ?? []}
              submitLabel="Save note"
              onCancel={() => setEditingNoteId(null)}
              onSubmit={(message, files, keepUrls) =>
                handleUpdateNote(editingNote.id, message, files, keepUrls)
              }
            />
          ) : (
            <NoteComposer
              avatarName={currentUserName}
              busy={store.noteSaving}
              onSubmit={handleAddNote}
            />
          )}
        </Box>
      ) : null}

      {!hasCustomerNote && !hasNotes ? (
        <CustomTypography
          variant="body2"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont, fontSize: 14, pl: 6 }}
        >
          No notes yet. Be the first to add a note.
        </CustomTypography>
      ) : (
        <Box>
          {hasCustomerNote ? (
            <NoteItem
              note={{
                id: "customer-note",
                note: customerNote!.trim(),
                created_at: "",
                created_by: "Customer",
              }}
              isCustomerNote
            />
          ) : null}
          {notes.map((item) =>
            editingNoteId === String(item.id) ? null : (
              <NoteItem
                key={item.id}
                note={item}
                currentUserName={currentUserName}
                busy={store.noteSaving}
                onEdit={
                  canEditNotes
                    ? (note) => setEditingNoteId(String(note.id))
                    : undefined
                }
                onDelete={canEditNotes ? handleOpenDeleteNote : undefined}
              />
            ),
          )}
        </Box>
      )}

      <ConfirmPopup
        mode="swayy"
        open={Boolean(notePendingDelete)}
        title="Delete note?"
        message="This note will be permanently deleted and cannot be recovered."
        cancelText="Keep note"
        confirmText={store.noteSaving ? "Deleting..." : "Delete note"}
        loading={store.noteSaving}
        error={deleteError}
        onCancel={handleCloseDeleteNote}
        onConfirm={() => {
          void handleConfirmDeleteNote();
        }}
      />
    </Box>
  );
};

export default observer(SwayyOrderNotesPanel);
